/*
 * What a stitch can be made with, and the stitched batch's start, judged the same
 * way on both screens. The backend owns which courses are eligible, which batches
 * are live and the earliest start; these only narrow its lists to the ones that
 * can follow this batch and check a start the ops user typed against its answer.
 */

import { isClassDay, toDisplayDate } from "./timings";

/**
 * The courses a new stitch batch can be created for: eligible and credit — a chain
 * is never flex (`stitch-flex`) — and never this batch's own course. The course's
 * follow-ons first, as the likeliest choice; otherwise the backend's order.
 * @param {{courseId: string, isSelectable: boolean, isFlex: boolean}[]} courses
 * @param {{courseId: string, continuationIds: string[]}} head - this batch's course
 *   and its follow-on courses
 * @returns {Object[]} the same rows, filtered and ordered
 */
export const stitchableCourses = (courses, { courseId, continuationIds }) =>
  (courses ?? [])
    ?.filter((option) => option?.isSelectable && !option?.isFlex && option?.courseId !== courseId)
    ?.sort(
      (a, b) =>
        Number(continuationIds?.includes(b?.courseId)) - Number(continuationIds?.includes(a?.courseId)),
    );

/**
 * The live batches that can follow this one: only a batch that starts after it
 * starts (linking one moves its start to after this batch ends).
 * @param {{startDate: string}[]} targets - mapped `stitchTargets`
 * @param {string} headStartDate - "YYYY-MM-DD"; empty while not typed
 * @returns {Object[]}
 */
export const targetsStartingAfter = (targets, headStartDate) =>
  (targets ?? [])?.filter(
    (option) => !headStartDate || !option?.startDate || option?.startDate > headStartDate,
  );

/**
 * Why a start the ops user typed for the stitched batch would be refused, or "":
 * before the backend's earliest start (`successor-start-too-early`), or off the
 * stitched batch's class days (`start-date-not-class-day`).
 * @param {Object} input
 * @param {string} input.typedStart - "YYYY-MM-DD"; "" follows the earliest start
 * @param {string} input.earliestStart - E3 `successor.earliestStart`; "" while unknown
 * @param {{day: string}[]} input.days - the stitched batch's class days
 * @returns {string}
 */
export const successorStartError = ({ typedStart, earliestStart, days }) => {
  if (!typedStart) return "";
  if (earliestStart && typedStart < earliestStart) {
    return `The stitched batch can start on ${toDisplayDate(earliestStart)} at the earliest.`;
  }
  if (days?.length && !isClassDay(typedStart, days)) {
    return "Pick one of the stitched batch's class days.";
  }
  return "";
};

/**
 * Where the backend's earliest start stands for the successor: on its way, or not
 * coming. A typed date is checked against that earliest and sent only once it is
 * known, so until then the save waits — a head schedule changed after a date was
 * typed must not let the old date through unchecked.
 * @param {Object} input
 * @param {string} input.earliestStart
 * @param {Object|null} input.preview - the successor's `usePreview` answer
 * @returns {{ isPending: boolean, isUnavailable: boolean }}
 */
export const earliestStartStateOf = ({ earliestStart, preview }) => ({
  isPending: !earliestStart && !preview?.error,
  isUnavailable: !earliestStart && !!preview?.error,
});

/**
 * The start that travels as `startingDate` (E4/E6, Q-D3): only one moved later than
 * the earliest, and only when valid — otherwise none, and the backend's earliest
 * applies. Never while the earliest is unknown: the date could not be checked.
 * @param {Object} input
 * @param {string} input.typedStart
 * @param {string} input.earliestStart
 * @param {string} input.error - `successorStartError`'s answer
 * @returns {string}
 */
export const movedStartOf = ({ typedStart, earliestStart, error }) =>
  typedStart && earliestStart && typedStart > earliestStart && !error ? typedStart : "";

/**
 * A new stitch batch's own figures from E1, sent with the head's preview so the
 * backend sizes it after the head (R-07-03, Q-QA7): its course's sessions left and
 * its purchase's room. Shaping only — the preview does the sum, because on one
 * purchase the head takes its share first.
 * @param {Object|null} course - a CourseVM
 * @returns {{sessionsLeft: number|null, capacityRemaining: number,
 *            extraCreditsUnallocated: number, masterOrderId: string|null}|null}
 */
export const successorFiguresOf = (course) =>
  course
    ? {
        sessionsLeft: course?.sessionsLeft ?? null,
        capacityRemaining: course?.purchase?.capacityRemaining ?? 0,
        extraCreditsUnallocated: course?.purchase?.extraCreditsUnallocated ?? 0,
        masterOrderId: course?.purchase?.masterOrderId ?? null,
      }
    : null;

/**
 * The head's preview body with the successor to answer for: its timings for the
 * earliest start, and on a new batch its figures for the ceiling.
 * @returns {Object|null}
 */
export const successorPreviewRequestOf = ({ headRequest, timings, figures }) =>
  headRequest && timings
    ? { ...headRequest, successor: { timings, ...(figures ? { figures } : {}) } }
    : null;

/**
 * The count a new stitch batch shows: the one the ops user typed, else the
 * backend's ceiling for it after the head (the preview's `successor.sessionCeiling`),
 * else its course's own prefill while that is on its way.
 * @param {{typed: string|null, ceiling: number|null, course: Object|null}} input
 * @returns {string}
 */
export const successorSessionsOf = ({ typed, ceiling, course }) => {
  if (typed !== null && typed !== undefined) return typed;
  const prefill = ceiling ?? course?.prefill ?? course?.sessions;
  return prefill === null || prefill === undefined ? "" : String(prefill);
};
